import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { queryKeys } from '../core/queries/queryKeys';
import { useCreditsPaymentStatusQuery, useSubscriptionPaymentStatusQuery } from '../core/queries/paymentStatus';
import { useAuthStore } from '../store/authStore';

function Success() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const refreshUser = useAuthStore((s) => s.refreshUser);
  const [countdown, setCountdown] = useState(5);
  const type = searchParams.get('type') || 'payment';
  const sessionId = searchParams.get('session_id');
  const isCredits = type === 'credits';
  const creditsStatusQuery = useCreditsPaymentStatusQuery(sessionId, {
    enabled: Boolean(sessionId && isCredits),
  });
  const subscriptionStatusQuery = useSubscriptionPaymentStatusQuery(sessionId, {
    enabled: Boolean(sessionId && !isCredits),
  });
  const statusQuery = isCredits ? creditsStatusQuery : subscriptionStatusQuery;
  const paymentStatus = statusQuery.data?.status;
  const paymentFailed =
    Boolean(sessionId) &&
    (statusQuery.isPollingTimedOut ||
      statusQuery.isError ||
      paymentStatus === 'failed' ||
      paymentStatus === 'expired');
  const waitingForConfirmation = Boolean(sessionId && paymentStatus !== 'paid' && !paymentFailed);

  useEffect(() => {
    if (!sessionId) return;

    queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
    refreshUser?.();
  }, [queryClient, refreshUser, sessionId]);

  useEffect(() => {
    if (paymentStatus !== 'paid') return;

    refreshUser?.();
    queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
    queryClient.invalidateQueries({ queryKey: ['history'] });
    queryClient.invalidateQueries({ queryKey: ['user-history'] });

    if (isCredits) {
      queryClient.invalidateQueries({ queryKey: queryKeys.creditsPackages() });
    } else {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionCurrent() });
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionPlans() });
    }
  }, [isCredits, paymentStatus, queryClient, refreshUser]);
  useEffect(() => {
    if (waitingForConfirmation || paymentFailed) return undefined;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [paymentFailed, waitingForConfirmation]);
  useEffect(() => {
    if (countdown === 0 && !waitingForConfirmation && !paymentFailed) {
      window.location.href = 'https://t.me/pro_ai_banana_bot/proAI';
    }
  }, [countdown, paymentFailed, waitingForConfirmation]);
  const handleReturnToTelegram = () => {
    window.location.href = 'https://t.me/pro_ai_banana_bot/proAI';
  };
  const getMessage = () => {
    if (waitingForConfirmation) {
      return {
        title: t('success.checkingPayment', 'Checking payment'),
        subtitle: t('success.waitingConfirmation', 'Waiting for payment confirmation...')
      };
    }
    if (paymentFailed) {
      return {
        title: t('success.paymentNotConfirmed', 'Payment not confirmed yet'),
        subtitle: t('success.confirmationTimeout', 'If you paid, reopen the app later and the balance will refresh automatically.')
      };
    }
    if (type === 'credits') {
      return {
        title: t('success.creditsAdded'),
        subtitle: t('success.creditsAvailable')
      };
    }
    return {
      title: t('success.subscriptionActive'),
      subtitle: t('success.planActivated')
    };
  };
  const { title, subtitle } = getMessage();
  return (
    <div className="min-h-screen bg-bg-app flex flex-col items-center justify-center p-6">
      <div className="bg-surface-2 rounded-3xl p-8 max-w-sm w-full text-center relative overflow-visible">
        {/* Logo - выходит за пределы карточки */}
        <div className="flex justify-center -mt-24 mb-4">
          <div className="w-[140px] h-[140px]">
            <img src="/img/banlogo.webp" alt="Nano Banana Pro" className="w-full h-full object-contain" style={{ imageRendering: 'auto' }} />
          </div>
        </div>
        {/* Title */}
        <h1 className="text-white text-2xl font-bold mb-2">
          {title}
        </h1>
        <p className="text-white/60 text-sm mb-8">
          {subtitle}
        </p>
        {/* Return Button */}
        <button
          onClick={handleReturnToTelegram}
          className="w-full h-14 bg-primary-btn-gradient text-black font-bold rounded-xl flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
        >
          {t("success.backToTelegram")}
          <ArrowRight className="w-5 h-5" />
        </button>
        {waitingForConfirmation && (
          <p className="text-white/40 text-xs mt-4">
            {t('success.waitingConfirmation', 'Waiting for payment confirmation...')}
          </p>
        )}
        {!waitingForConfirmation && countdown > 0 && (
          <p className="text-white/40 text-xs mt-4">
            {t("success.autoRedirect", { seconds: countdown })}
          </p>
        )}
        {!waitingForConfirmation && countdown === 0 && (
          <p className="text-white/40 text-xs mt-4">
            {t("success.clickAbove")}
          </p>
        )}
      </div>
    </div>
  );
}
export default Success;
